import React, {
  useMemo, useEffect, useCallback, useState,
} from 'react';

import { InfoCircleFilledIcon } from '@vlabs/icons';
import type {
  TCandidateOrigin, TEventReferencePreview, TMatcherType, TReference, TReferencePreview, TReferenceType,
} from '@vlabs/monokit/entities/matcher/types/types';
import type { TFileDropzoneFile } from '@vlabs/monokit/features/file-with-detections-viewer/FileWithDetectionsViewer';
import type { TDetection } from '@vlabs/shared/components/photo-upload-form';
import { permissions } from '@vlabs/shared/config';
import {
  ButtonSwitch, ButtonSwitchGroup, Panel,
} from '@vlabs/uikit';
import debounce from 'lodash/debounce';
import qs from 'qs';
import { useLocation } from 'react-router-dom';

import { EventInput, FaceInput } from './inputs';
import { ReferencePreview } from './reference-preview/ReferencePreview';
import { ReferenceDetectionsList } from './ReferenceDetectionsList';
import st from './ReferenceSelector.module.sass';
import { SdkDescriptorSearch } from './SdkDescriptorSearch';
import { useDetections } from './useDetections';
import { useSearchApi } from '../../api/SearchApiProvider';
import { useSearchPageTranslation } from '../../i18n';
import { useSearch } from '../search-provider/SearchProvider';

type TCan = (arg: string[], arg2?: Record<string, string>) => boolean;

const LEGACY_REFERENCE_TYPES_ORDER: TReferenceType[] = [
  'face_external_id', 'sdk_descriptor', 'event', 'face',
];

function isReferencePreviewAvailable(
  ref: { type: TReferenceType } | undefined,
): ref is TReferencePreview {
  if (!ref) return false;

  return (
    (ref.type === 'face' || ref.type === 'face_external_id' || ref.type === 'event')
  );
}

function canReferenceType(can: TCan, type: TReferenceType): boolean {
  switch (type) {
    case 'sdk_descriptor':
      return can([permissions.resources.sdk]);
    case 'event':
      return can([permissions.event.matching]);
    case 'face':
      return can([permissions.face.matching]);
    case 'face_external_id':
      return true;
    default:
      return false;
  }
}

function ReferenceSelector({
  can,
  maxImageSize = undefined,
  eventPreview = undefined,
}: {
  can: TCan;
  maxImageSize?: number | null;
  eventPreview?: (props: { reference: TEventReferencePreview }) => JSX.Element;
}): JSX.Element {
  const { t } = useSearchPageTranslation();
  const { search } = useLocation();

  const [inputValue, setInputValue] = useState<string>('');
  const [inputError, setInputError] = useState<string>('');
  const [file, setFile] = useState<TFileDropzoneFile>();
  const [referenceType, setReferenceType] = useState<TReferenceType>('sdk_descriptor');
  const [referencesPreview, setReferencesPreview] = useState<TReferencePreview[]>([]);
  const [selectedDetections, setSelectedDetections] = useState<TDetection[]>([]);
  const [selectedDetectionsId, setSelectedDetectionsId] = useState<string[]>([]);

  const {
    setMatcherType,
    setReferences,
    resetSearchState,
    setSearchResults,
    setCandidateOrigin,
  } = useSearch();
  const api = useSearchApi();
  const { detections, isLoading: isDetectionsLoading } = useDetections(file);

  const referencePreview = useMemo(() => ({
    ...referencesPreview?.[0],
    type: referenceType,
  }), [referencesPreview, referenceType]);

  const resetSearchInfo = useCallback(() => {
    resetSearchState();
    setReferences([]);
    setSelectedDetectionsId([]);
    setFile(undefined);
    setInputError('');
  }, [resetSearchState, setReferences]);

  const handleSetReferences = useCallback(async (imageReferences: TReference[]) => {
    if (!(imageReferences?.length > 0)) {
      setReferencesPreview([]);
      return;
    }
    setReferences(imageReferences);
    let extendedReferenceInfo;

    try {
      if (!imageReferences?.[0]) return;
      extendedReferenceInfo = await api.getReferenceInfo(imageReferences?.[0]);
    } catch (error) {
      if (referenceType !== 'sdk_descriptor') {
        resetSearchState();
        setReferencesPreview([]);
      }
      throw error;
    }
    setReferencesPreview(extendedReferenceInfo ?? []);
  }, [api, referenceType, resetSearchState, setReferences]);

  useEffect(() => {
    resetSearchInfo();

    const queryParams = qs.parse(search, { ignoreQueryPrefix: true });
    if (!Object.keys(queryParams).length) return;
    if (!Array.isArray(queryParams.references)) return;
    const reference = queryParams.references[0] as
      | { id: string; referenceType: TReferenceType; candidateOrigin?: TCandidateOrigin }
      | undefined;
    if (!reference) return;

    setInputValue(reference.id);
    setReferenceType(reference.referenceType);
    if (reference?.candidateOrigin) setCandidateOrigin(reference.candidateOrigin);

    handleSetReferences([{ id: reference.id, type: reference.referenceType }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const supportedTypes = new Set(api.capabilities.referenceTypes);
    const availableOrigin = LEGACY_REFERENCE_TYPES_ORDER.filter(
      (type) => supportedTypes.has(type) && canReferenceType(can, type),
    );

    if (!availableOrigin.includes(referenceType)) {
      setReferenceType(availableOrigin[0] as TReferenceType);
      setInputValue('');
      resetSearchInfo();
      setMatcherType('face');
    }

    return () => {
      resetSearchInfo();
      setCandidateOrigin('events');
      setReferenceType('sdk_descriptor');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (detections === undefined
      || detections?.all?.length === 0
      || !(selectedDetectionsId?.length > 0)) return;

    const imageReferences = selectedDetectionsId.map((detectionId) => {
      setMatcherType(detections.byId[detectionId]?.type as unknown as TMatcherType);
      if (detections.byId[detectionId]?.type === 'body' && can([permissions.event.view, permissions.event.matching], { method: 'allof' })) {
        setCandidateOrigin('events');
      }
      return {
        id: detectionId,
        type: referenceType,
        data: detections.byId[detectionId]?.attributes?.descriptor?.sdk_descriptor,
      };
    });

    handleSetReferences(imageReferences);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detections, selectedDetectionsId, setReferences, referenceType]);

  const onSelectHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDetection = detections?.all.find((detection) => detection.id === event.target.value);
    if (selectedDetection) {
      setSelectedDetections([selectedDetection]);
    }
  }, [detections, setSelectedDetections]);

  const resetFile = () => {
    setFile(undefined);
    setSelectedDetectionsId([]);
    resetSearchState();
    setReferences([]);
    setReferencesPreview([]);
  };

  const onFileSelect = (newFile: TFileDropzoneFile) => {
    if (!newFile) return;
    setFile(newFile);
  };

  const resetInput = () => {
    resetSearchState();
    setReferences([]);
    setReferencesPreview([]);
    setInputError('');
    setInputValue('');
  };

  const setReferencesDebounced = useMemo(
    () => debounce(handleSetReferences, 1000),
    [handleSetReferences],
  );

  const onInputChange = (value: string | undefined, {
    validationError = undefined,
    debounced = false,
  }: {
    validationError?: string | undefined;
    debounced?: boolean;
  }) => {
    if (!value) {
      resetInput();
      return;
    }
    setInputValue(value);

    if (typeof validationError === 'string') {
      setInputError(validationError);
      return;
    }

    setInputError('');
    setSearchResults(undefined);
    if (debounced) {
      setReferencesDebounced([{ id: value, type: referenceType }]);
    } else {
      handleSetReferences([{ id: value, type: referenceType }]);
    }
  };

  const onChangeDetections = (v: string[]) => {
    setSearchResults(undefined);
    setSelectedDetectionsId(v);
  };

  const onChangeReferenceType = (v: TReferenceType) => {
    setReferenceType(v);
    setInputValue('');
    setInputError('');
    resetFile();
    resetSearchInfo();
    setMatcherType('face');
  };

  return (
    <Panel className={st.Root}>
      <div className={st.Input}>
        <div>{t('reference_selector.title')}</div>

        <ButtonSwitchGroup
          data-testid="inputTypeButtonGroup"
          onChange={onChangeReferenceType}
          size="medium"
          value={referenceType}
          variant="square"
        >
          <ButtonSwitch
            disabled={!can([permissions.resources.sdk])}
            value="sdk_descriptor"
          >
            {t('reference_selector.search_type_switch.buttons.photo')}
          </ButtonSwitch>
          <ButtonSwitch
            disabled={!can([permissions.event.matching])}
            value="event"
          >
            {t('reference_selector.search_type_switch.buttons.event')}
          </ButtonSwitch>
          <ButtonSwitch
            disabled={!can([permissions.face.matching])}
            value="face"
          >
            {t('reference_selector.search_type_switch.buttons.face')}
          </ButtonSwitch>
        </ButtonSwitchGroup>

        {referenceType === 'sdk_descriptor' && (
          <SdkDescriptorSearch
            detections={detections?.all}
            file={file}
            isLoading={isDetectionsLoading}
            maxImageSize={maxImageSize}
            onFileSelect={onFileSelect}
            onResetFile={resetFile}
            onSelectSampleId={onChangeDetections}
            selectedDetections={selectedDetections}
            setSelectedDetections={setSelectedDetections}
          />
        )}

        {referenceType === 'event' && (
          <EventInput
            inputError={inputError}
            inputValue={inputValue}
            onInputChange={onInputChange}
            resetInput={resetInput}
          />
        )}

        {(referenceType === 'face' || referenceType === 'face_external_id') && (
          <FaceInput
            inputError={inputError}
            inputValue={inputValue}
            onInputChange={onInputChange}
            onReferenceTypeChange={onChangeReferenceType}
            referenceType={referenceType}
            resetInput={resetInput}
          />
        )}
      </div>
      <div className={st.VerticalDivider} />
      <div className={st.References}>
        {file !== undefined && !!detections?.all && detections?.all?.length > 0 && (
          <React.Fragment>
            <div className={st.Divider} />
            <div>{t('reference_selector.image_input.title')}</div>
            <div className={st.Info}>
              <InfoCircleFilledIcon />
              <div>{t('reference_selector.image_input.info.label')}</div>
            </div>
          </React.Fragment>
        )}
        {referencesPreview?.length > 0
          && isReferencePreviewAvailable(referencePreview)
          && (
          <ReferencePreview
            eventPreview={eventPreview}
            reference={referencePreview}
          />
          )}

        {referenceType === 'sdk_descriptor'
          && file !== undefined
          && !!detections
          && detections.all.length > 0
          && (
            <ReferenceDetectionsList
              detections={detections}
              onSelectDetection={onSelectHandler}
              selectedDetections={selectedDetections}
            />
          )}
      </div>
    </Panel>
  );
}

export { ReferenceSelector };
